/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import MomentService from 'cio/services/moment';
import moment from 'moment-timezone';
import { timestampToInlineLongDate, dateIsWithinOneDayFromNow } from 'cio/utils/time';
import { controlSupportTeamAccessFieldValidation } from 'cio/components/support-form/control-team-access-field';
import FormValidations from 'cio/utils/form-validations';
import {
  DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
  DEFAULT_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS,
  MIN_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS,
  MAX_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS,
} from 'cio/models/account';
import UserModel from 'cio/models/user';
import { isEmpty } from '@ember/utils';

const SAVE_SETTINGS_ERROR_MESSAGE = 'Could not update your account settings. Please try again.';

const controlSuccessTeamAccessDaysFieldValidation = {
  daysLimit(value: number) {
    if (!/^\d+$/.test(value as unknown as string)) {
      return 'Please enter a valid number.';
    }

    if (value < MIN_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS || value > MAX_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS) {
      return `The number of days must be between ${MIN_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS} and ${MAX_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS} days.`;
    }

    return true;
  },
};

interface AccessControlsArgs {
  expandAccessControls: boolean;
}

export default class AccessControls extends Component<AccessControlsArgs> {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service moment!: MomentService;

  get accessControls() {
    return Object.freeze([
      {
        id: 'successTeamAccessEnabled',
        premiumOnly: true,
        label: 'Customer Success access',
        description:
          '<p>As part of your Premium service, you receive support and consultation from a Customer Success Manager (CSM). To better serve you, this CSM may require access to your account. If you choose to enable access, only your dedicated CSM will have write-access; all other members of the Customer Success team will have read-only access to assist as needed. If you have questions, your CSM will be happy to discuss this with you.</p><p>Access will automatically be removed once the time set has expired. You may also revoke access at any time.</p>',
        accessEnabled: this.account.current.successTeamAccessEnabled,
        accessHistoryText: this.successTeamAccessConsentHistoryText,
        showAccessHistoryText:
          this.account.current.csAccessGrantedBy.get('email') != null &&
          !isEmpty(this.successTeamAccessConsentHistoryText),
        readOnly: !this.auth.hasPermission('account/support_access/edit'),
        disabledTooltip: 'Only administrators, workspace managers, and authors can change this setting.',
        task: 'confirmSuccessAccessPreferenceTask',
      },
      {
        id: 'supportTeamAccessEnabled',
        premiumOnly: false,
        label: 'Support access',
        description:
          '<p>To resolve issues, our support team may need access to your account. If you choose to enable access, only authorized team members will have access while they help with your requests.</p><p>Access will automatically be removed once the time set has expired. You may also revoke access at any time.</p>',
        accessEnabled: this.account.current.supportTeamAccessEnabled,
        accessHistoryText: this.supportTeamAccessConsentHistoryText,
        showAccessHistoryText:
          this.account.current.accessGrantedBy.get('email') != null &&
          !isEmpty(this.supportTeamAccessConsentHistoryText),
        readOnly: !this.auth.hasPermission('account/support_access/edit'),
        disabledTooltip: 'Only administrators, workspace managers, and authors can change this setting.',
        task: 'confirmSupportAccessPreferenceTask',
      },
    ]);
  }

  private generateModalTitle(team: string, accessEnabled: boolean) {
    let verb = accessEnabled ? 'Edit' : 'Enable';
    return `${verb} ${team} team access`;
  }

  get successTeamAccessModalTitle() {
    return this.generateModalTitle('Customer Success', this.account.current.successTeamAccessEnabled);
  }

  get supportTeamAccessModalTitle() {
    return this.generateModalTitle('Support', this.account.current.supportTeamAccessEnabled);
  }

  private formatEndOfAccessPeriod(daysLimit: number) {
    let chosenDaysInSeconds = daysLimit * 24 * 60 * 60;
    return moment.unix(this.moment.currentTimestamp + chosenDaysInSeconds).format('MMMM DD, YYYY');
  }

  get endOfCurrentSupportAccessPeriod() {
    return this.formatEndOfAccessPeriod(this.supportTeamAccessFormValues.daysLimit);
  }

  get endOfCurrentSuccessAccessPeriod() {
    return this.formatEndOfAccessPeriod(this.successTeamAccessFormValues.daysLimit);
  }

  @computed('account.current.{premium,premiumTrial,hasCsm}')
  get displayPremiumAccountSettings() {
    let account = this.account.current;
    if (account == null) {
      return false;
    }
    return account.premium || account.premiumTrial || account.hasCsm;
  }

  private generateConsentSetByUserEmail(user: UserModel) {
    return `by ${user?.get('email')}` ?? '';
  }

  @computed('account.current.accessGrantedBy.email')
  get supportTeamAccessConsentSetByUserEmail() {
    return this.generateConsentSetByUserEmail(this.account.current.accessGrantedBy);
  }

  @computed('account.current.csAccessGrantedBy.email')
  get successTeamAccessConsentSetByUserEmail() {
    return this.generateConsentSetByUserEmail(this.account.current.csAccessGrantedBy);
  }

  @computed('account.current.accessGrantedOn', 'moment.currentTimestamp')
  get supportTeamAccessConsentSetOnText() {
    return this.formatAccessConsentDate(this.account.current.accessGrantedOn);
  }

  @computed('account.current.accessGrantedUntil', 'moment.currentTimestamp')
  get supportTeamAccessConsentGrantedUntilText() {
    return this.formatAccessConsentDate(this.account.current.accessGrantedUntil);
  }

  @computed('account.current.csAccessGrantedOn', 'moment.currentTimestamp')
  get successTeamAccessConsentSetOnText() {
    return this.formatAccessConsentDate(this.account.current.csAccessGrantedOn);
  }

  @computed('account.current.csAccessGrantedUntil', 'moment.currentTimestamp')
  get successTeamAccessConsentGrantedUntilText() {
    return this.formatAccessConsentDate(this.account.current.csAccessGrantedUntil);
  }

  formatAccessConsentDate(timestamp?: number) {
    if (isEmpty(timestamp)) {
      return '';
    }

    let formattedDate = timestampToInlineLongDate(timestamp, this.moment);
    return `${dateIsWithinOneDayFromNow(timestamp ?? 0) ? '' : 'on'} ${formattedDate}`;
  }

  @computed(
    'account.current.supportTeamAccessEnabled',
    'supportTeamAccessConsentSetByUserEmail',
    'supportTeamAccessConsentSetOnText',
    'supportTeamAccessConsentGrantedUntilText',
    'moment.currentTimestamp',
  )
  get supportTeamAccessConsentHistoryText() {
    return this.formatAccessSettingsHistoryText({
      accessGranted: this.account.current.supportTeamAccessEnabled,
      accessGrantedUntilTimestamp: this.account.current.accessGrantedUntil,
      accessGrantedUntilText: this.supportTeamAccessConsentGrantedUntilText,
      accessSetByUserEmail: this.supportTeamAccessConsentSetByUserEmail,
      accessSetOnText: this.supportTeamAccessConsentSetOnText,
      accessCanBeGrantedIndefinitely: false,
    });
  }

  @computed(
    'account.current.successTeamAccessEnabled',
    'successTeamAccessConsentSetByUserEmail',
    'successTeamAccessConsentSetOnText',
    'successTeamAccessConsentGrantedUntilText',
    'moment.currentTimestamp',
  )
  get successTeamAccessConsentHistoryText() {
    return this.formatAccessSettingsHistoryText({
      accessGranted: this.account.current.successTeamAccessEnabled,
      accessGrantedUntilTimestamp: this.account.current.csAccessGrantedUntil,
      accessGrantedUntilText: this.successTeamAccessConsentGrantedUntilText,
      accessSetByUserEmail: this.successTeamAccessConsentSetByUserEmail,
      accessSetOnText: this.successTeamAccessConsentSetOnText,
      accessCanBeGrantedIndefinitely: true,
    });
  }

  formatAccessSettingsHistoryText(data: {
    accessGranted: boolean;
    accessGrantedUntilTimestamp: number;
    accessGrantedUntilText: string;
    accessSetByUserEmail: string;
    accessSetOnText: string;
    accessCanBeGrantedIndefinitely: boolean;
  }) {
    // Metadata may be not set for some accounts.
    let hasAccessConsentMetadata = data.accessSetByUserEmail || data.accessSetOnText;

    if (!data.accessGranted && !hasAccessConsentMetadata) {
      return '';
    }

    let text = 'Access ';
    text += data.accessGranted ? 'granted ' : 'revoked ';

    let accessGrantedIndefinitely =
      data.accessGranted && data.accessCanBeGrantedIndefinitely && data.accessGrantedUntilTimestamp === -1;
    if (accessGrantedIndefinitely) {
      text += 'indefinitely ';
    }

    if (hasAccessConsentMetadata) {
      let accessHasBeenRevokedAutomatically = !data.accessGranted && data.accessGrantedUntilTimestamp > 0;
      if (accessHasBeenRevokedAutomatically) {
        text += `automatically ${data.accessGrantedUntilText}`;
      } else {
        text += data.accessSetByUserEmail ? ` ${data.accessSetByUserEmail} ` : '';
        text += data.accessSetOnText;
      }
    }

    if (data.accessGranted && !accessGrantedIndefinitely) {
      text += ` and will be revoked ${data.accessGrantedUntilText}`;
    }

    return `${text}.`;
  }

  @tracked isShowingSuccessAccessConfirmationModal = false;
  @tracked isShowingSupportAccessConfirmationModal = false;

  @tracked supportTeamAccessFormValidations: FormValidations;
  @tracked supportTeamAccessFormValues = {
    daysLimit: DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
  };
  @tracked isRevokingSupportTeamAccess = false;

  @tracked successTeamAccessFormValidations: FormValidations;
  @tracked successTeamAccessFormValues = {
    daysLimit: DEFAULT_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS,
    indefiniteAccess: false,
  };
  @tracked isRevokingSuccessTeamAccess = false;

  constructor(owner: unknown, args: AccessControlsArgs) {
    super(owner, args);
    this.supportTeamAccessFormValidations = FormValidations.create({
      validations: controlSupportTeamAccessFieldValidation,
      context: this,
      modelKey: 'supportTeamAccessFormValues',
    });
    this.successTeamAccessFormValidations = FormValidations.create({
      validations: controlSuccessTeamAccessDaysFieldValidation,
      context: this,
      modelKey: 'successTeamAccessFormValues',
    });
  }

  get minSuccessTeamAccessDays() {
    return MIN_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS;
  }

  get maxSuccessTeamAccessDays() {
    return MAX_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS;
  }

  confirmSuccessAccessPreferenceTask = dropTask(async () => {
    this.isShowingSuccessAccessConfirmationModal = true;
  });

  confirmSupportAccessPreferenceTask = dropTask(async () => {
    this.isShowingSupportAccessConfirmationModal = true;
  });

  @action
  async validateSupportTeamAccessFormValues() {
    if (!this.isRevokingSupportTeamAccess) {
      try {
        await this.supportTeamAccessFormValidations.validateModel();
      } catch {
        return;
      }
    }
  }

  @action
  async validateSuccessTeamAccessFormValues() {
    if (!this.isRevokingSuccessTeamAccess) {
      try {
        await this.successTeamAccessFormValidations.validateModel();
      } catch {
        return;
      }
    }
  }

  changeSupportAccessTimeLimitTask = dropTask(async () => {
    let account = this.account.current;

    await this.validateSupportTeamAccessFormValues();

    try {
      await account.setSupportAccessConsent(this.supportTeamAccessFormValues.daysLimit);
    } catch {
      this.alerts.error('privacy.update-support-access', SAVE_SETTINGS_ERROR_MESSAGE);
    } finally {
      this.isShowingSupportAccessConfirmationModal = false;
      this.supportTeamAccessFormValues.daysLimit = DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS;
      this.isRevokingSupportTeamAccess = false;
    }
  });

  changeSuccessAccessTimeLimitTask = dropTask(async () => {
    let account = this.account.current;

    await this.validateSuccessTeamAccessFormValues();

    try {
      let options = {
        days: this.successTeamAccessFormValues.daysLimit,
        infinite: this.successTeamAccessFormValues.indefiniteAccess,
      };
      await account.setSuccessAccessConsent(options);
    } catch {
      this.alerts.error('privacy.update-success-access', SAVE_SETTINGS_ERROR_MESSAGE);
    } finally {
      this.isShowingSuccessAccessConfirmationModal = false;
      this.successTeamAccessFormValues = {
        daysLimit: DEFAULT_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS,
        indefiniteAccess: false,
      };
      this.isRevokingSuccessTeamAccess = false;
    }
  });

  @action
  async revokeSupportTeamAccess() {
    this.isRevokingSupportTeamAccess = true;
    this.supportTeamAccessFormValues.daysLimit = 0;
    await this.changeSupportAccessTimeLimitTask.perform();
  }

  @action
  async revokeSuccessTeamAccess() {
    this.isRevokingSuccessTeamAccess = true;
    this.successTeamAccessFormValues.daysLimit = 0;
    this.successTeamAccessFormValues.indefiniteAccess = false;
    await this.changeSuccessAccessTimeLimitTask.perform();
  }

  @action
  onSupportTeamAccessTimeLimitChanged(value: number) {
    this.supportTeamAccessFormValidations.clearAttributeErrors('daysLimit');
    this.supportTeamAccessFormValues = { ...this.supportTeamAccessFormValues, daysLimit: value };
  }

  @action
  onSuccessTeamAccessTimeLimitChanged(value: number) {
    this.successTeamAccessFormValidations.clearAttributeErrors('daysLimit');
    this.successTeamAccessFormValues = { ...this.successTeamAccessFormValues, daysLimit: value };
  }

  @action
  async onSuccessTeamAccessDaysOptionSelected() {
    await this.validateSuccessTeamAccessFormValues();
    this.successTeamAccessFormValues = { ...this.successTeamAccessFormValues, indefiniteAccess: false };
  }

  @action
  async onSuccessTeamAccessIndefiniteOptionSelected() {
    this.successTeamAccessFormValidations.clearAttributeErrors('daysLimit');
    this.successTeamAccessFormValues = { ...this.successTeamAccessFormValues, indefiniteAccess: true };
  }
}
